import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Text, Icon, DateInput, TimeInput } from '@/ui-kits';
import { useDispatch } from 'react-redux';
import { scheduleMeetRequest } from '@/services/redux/slices'; 
import { cn } from '@/utils/common_functions';

interface ConnectedProvider {
  provider: number;
  is_connected: boolean;
}

interface ScheduleMeetModalProps {
  isOpen: boolean;
  onClose: () => void;
  case_id: number;
  connectedProviders?: ConnectedProvider[];
}

export const ScheduleMeetModal: React.FC<ScheduleMeetModalProps> = ({
  isOpen,
  onClose,
  case_id,
  connectedProviders = []
}) => {
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      setDate(null);
      setTime(null);
      if (connectedProviders && connectedProviders.length > 0) {
        setSelectedProvider(connectedProviders[0].provider);
      }
    }
  }, [isOpen]);

  const isMultiProvider = connectedProviders.length > 1;
  const singleProvider = connectedProviders.length === 1 ? connectedProviders[0] : null;

  const minTime = useMemo(() => {
    const now = new Date();
    if (
      date &&
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    ) {
      return now;
    }
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay;
  }, [date]);

  const maxTime = useMemo(() => {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  }, []);

  const handleDateChange = (val: Date | null) => {
    setDate(val);
    if (val && time) {
      const now = new Date();
      if (
        val.getDate() === now.getDate() &&
        val.getMonth() === now.getMonth() &&
        val.getFullYear() === now.getFullYear()
      ) {
        const selectedTimeDate = new Date();
        selectedTimeDate.setHours(time.getHours(), time.getMinutes(), 0, 0);
        if (selectedTimeDate < now) {
          setTime(null);
        }
      }
    }
  };

  const handleSchedule = () => {
    if (!date || !time) return;
    
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    const hours = time.getHours();
    const minutes = time.getMinutes();
    
    const meetDate = new Date(year, month, day, hours, minutes, 0);
    const meet_time = meetDate.toISOString();
    
    setIsLoading(true);
    dispatch(scheduleMeetRequest({
      id: case_id,
      meet_time: meet_time,
      provider: selectedProvider,
      onSuccess: () => {
        setIsLoading(false);
        onClose();
      },
      onError: () => {
        setIsLoading(false);
      }
    }));
  };

  return (
    <Modal open={isOpen} onClose={onClose} className="rounded-lg w-full max-w-[420px] p-6 bg-white overflow-visible">
      <div className="flex justify-between items-center mb-4">
        <div>
          <Text variant="title" className="!font-FigtreeBold text-ui_blue">
            Schedule Video Meeting
          </Text>
          {singleProvider && (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <Text variant="small" className="text-emerald-700 font-FigtreeMedium text-xs">
                Platform: {singleProvider.provider === 1 ? 'Google Meet' : 'Microsoft Teams'}
              </Text>
            </div>
          )}
        </div>
        <button onClick={onClose}>
          <Icon type="close" className="size-6" />
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {/* Provider Selection (Shown ONLY when >1 provider is connected) */}
        {isMultiProvider && (
          <div className="flex flex-col gap-2">
            <Text variant="body" className="!font-FigtreeSemiBold inline-flex gap-1 text-ui_blue">
              Select Video Provider
            </Text>
            <div className="grid grid-cols-2 gap-3">
              {connectedProviders.map((p) => {
                const isSelected = selectedProvider === p.provider;
                const isGoogle = p.provider === 1;
                const name = isGoogle ? 'Google Meet' : 'MS Teams';
                const iconType = isGoogle ? 'google_meet' : 'microsoft_teams';

                return (
                  <button
                    key={p.provider}
                    type="button"
                    onClick={() => setSelectedProvider(p.provider)}
                    className={cn(
                      'flex items-center gap-2.5 p-2.5 rounded-lg border-2 transition-all text-left',
                      isSelected
                        ? 'border-ui_blue bg-ui_sky_blue shadow-sm'
                        : 'border-ui_input_border bg-white hover:bg-gray-50'
                    )}
                  >
                    <Icon type={iconType} className="size-6 flex-shrink-0" />
                    <span className={cn('text-sm font-FigtreeSemiBold', isSelected ? 'text-ui_blue' : 'text-deep_blue')}>
                      {name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1 z-[999]">
          <DateInput 
            label="Date"
            value={date}
            onChangeDate={handleDateChange}
            placeholder="Select Date"
            minValue={new Date()}
            disablePortal={true}
          />
        </div>
        <div className="flex flex-col gap-1 z-[998]">
          <TimeInput 
            label="Time"
            value={time}
            onChangeTime={(val) => setTime(val)}
            placeholder="Select Time"
            disablePortal={true}
            minTime={minTime}
            maxTime={maxTime}
          />
        </div>
        <button 
          onClick={handleSchedule}
          disabled={!date || !time || isLoading}
          className="mt-4 bg-ui_blue text-white py-2.5 rounded-lg font-FigtreeBold disabled:opacity-50 hover:opacity-95 transition-opacity"
        >
          {isLoading ? 'Scheduling...' : 'Schedule & Send Invite'}
        </button>
      </div>
    </Modal>
  );
};
